import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { environment } from '@environments/environment';
import { Community } from '@app/_models/community';
import { PropertyAccess, MessageResponse } from '../../app/_models/responses';
import { ToastrService } from 'ngx-toastr';
import { Devices } from '@app/_models/devices';
import { PropertySubscriptions } from '@app/_models/propery.model';
import { AccessLog } from '@app/_models/access-log.model';

export type GuestDeviceOperation = 'TOGGLE' | 'DELAY' | 'ON/OFF';

export interface GuestDeviceDetails {
  label?: string;
  labeltxt?: string;
  device_type?: string;
  operation_modes?: string[];
}

@Injectable({ providedIn: 'root' })
export class AccessManagementService {
    constructor(private http: HttpClient, private toastr: ToastrService) { }

    getAllAccess(username: string) {
      return this.http.get<PropertyAccess[]>(`${environment.apiUrl}/api/access/user/`+username);
    }

    getProperyAccessLogs(propertyid: number, pagesize: number) {
      return this.http.get<AccessLog[]>(`${environment.apiUrl}/api/accesslog/pagesize/${pagesize}/property/` + propertyid);
    }

    getPropertySubscriptions(username: string) {
      return this.http.get<PropertySubscriptions>(`${environment.apiUrl}/api/user/property/subscriptions/`+username);
    }

    getDeviceSubscriptions(username: string) {
      return this.http.get<Devices>(`${environment.apiUrl}/api/user/device/subscriptions/`+username);
    }

    grantAccess(formdata: any) {
        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        return this.http.post<MessageResponse>(`${environment.apiUrl}/api/access/user`, formdata, { headers : header});
    }

    deleteAccess(id: number) {
      return this.http.delete<MessageResponse>(`${environment.apiUrl}/api/access/request/manage/` + id)
              .subscribe(data => {
                this.toastr.success(data.message, 'Success');
              }, error => {
                this.toastr.error(error.message, 'Error');
              });
    }

    denyAccess(formdata: any) {
        if (!environment.production) {
          console.log('Form data: remove access: ', formdata);
        }

        let header = new HttpHeaders();
        header = header.append('content-type', 'application/json');

        const options = {
            headers: new HttpHeaders({
              'Content-Type': 'application/json'
            }),
            body: formdata
          }

        return this.http.delete<MessageResponse>(`${environment.apiUrl}/api/access/user`, options)
          .subscribe(data => {
            this.toastr.success(data.message, 'Success');
          },
          error => {
               this.toastr.error(error.message, 'Error');

          }
        );
    }

    createGuestAccessRequest(formdata: any) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');

      return this.http.post<MessageResponse>(`${environment.apiUrl}/api/guest`, formdata, { headers : header});
    }

    updateGuestAccessRequest(formdata: any) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');

      let guestid = formdata.id;

      return this.http.put<MessageResponse>(`${environment.apiUrl}/api/guest/`+guestid, formdata, { headers : header});
    }

    viewGuestAccessRequestPropertyId(propertyid: number) {
      return this.http.get<any>(`${environment.apiUrl}/api/guest/property/` + propertyid);
    }

    viewGuestAccessRequestEmail(email: string) {
      return this.http.get<any>(`${environment.apiUrl}/api/guest/email/` + email);
    }

    viewGuestAccessRequestId(guestid: string) {
      return this.http.get<any>(`${environment.apiUrl}/api/guest/guestid/` + guestid);
    }

    getManageablePropertyRoles() {
      return this.http.get<any[]>(`${environment.apiUrl}/api/property/roles`);
    }

    getGuestPropertyDevices(propertyid: number) {
      return this.http.get<any[]>(`${environment.apiUrl}/api/devices/property/` + propertyid);
    }

    approveOrDenyGuestRequest(guestid: number, formdata: any) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');

      return this.http.put<MessageResponse>(`${environment.apiUrl}/api/guest/approval/` + guestid, formdata, { headers: header });
    }

    revokeGuestAccess(guestid: number, formdata: any) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');

      return this.http.put<MessageResponse>(`${environment.apiUrl}/api/guest/` + guestid, formdata, { headers: header });
    }

    guestLogin(email: string, accesskey: string) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');

      return this.http.post<any>(`${environment.apiUrl}/guest/login`, { email, accesskey }, { headers: header });
    }

    getGuestSubscriptions(email: string, guestAccessToken: string) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');
      if (guestAccessToken && guestAccessToken.trim()) {
        header = header.append('Authorization', `Bearer ${guestAccessToken.trim()}`);
      }

      return this.http.post<any>(`${environment.apiUrl}/api/guest/subscriptions`, { email }, { headers: header });
    }

    getGuestDeviceDetails(id: number, guestAccessToken: string) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${guestAccessToken.trim()}`
      });
      return this.http.get<GuestDeviceDetails>(`${environment.apiUrl}/api/guest/devices/${id}`, { headers });
    }

    operateGuestSmartDevice(id: number, operation: GuestDeviceOperation, nextState: boolean, duration: number, guestAccessToken: string) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${guestAccessToken.trim()}`
      });
      return this.http.post<MessageResponse>(`${environment.apiUrl}/api/guest/devices/${id}/operate`, {
        operation_type: operation,
        status: nextState ? 'ON' : 'OFF',
        operation_time: duration
      }, { headers });
    }

    operateGuestDevice(formdata: any, guestAccessToken: string) {
      let header = new HttpHeaders();
      header = header.append('content-type', 'application/json');
      if (guestAccessToken && guestAccessToken.trim()) {
        header = header.append('Authorization', `Bearer ${guestAccessToken.trim()}`);
      }

      return this.http.post<any>(`${environment.apiUrl}/api/guest/devices/operate`, formdata, { headers: header });
    }


}
